import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";

import {
  FileText,
  Search,
  Download,
  Printer,
  RefreshCcw,
  Calendar,
  Building2,
  User,
  CreditCard,
  Briefcase,
  Eye,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const BonusMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";

  // Filters
  const [years, setYears] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [selectedYear, setSelectedYear] = useState("Select");
  const [selectedBranch, setSelectedBranch] = useState("Select");
  const [selectedPayment, setSelectedPayment] = useState("Select");
  const [selectedDept, setSelectedDept] = useState("Select");

  // Data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-53", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // Fetch Years
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/bonus-master/years`);
        if (res?.data?.success) {
          setYears(res.data.years || []);
          
          // Set default year based on current month
          const currentYear = new Date().getFullYear();
          const currentMonth = new Date().getMonth() + 1; // 1-12
          
          if (currentMonth >= 10 && currentMonth <= 12) {
            // Oct-Dec, select next year
            setSelectedYear((currentYear + 1).toString());
          } else {
            // Jan-Sep, select current year
            setSelectedYear(currentYear.toString());
          }
        }
      } catch (err) {
        toast.error("Failed to load years");
        console.error("Years Error:", err);
      }
    };
    fetchYears();
  }, []);

  // Fetch Branches
  useEffect(() => {
    const fetchBranches = async () => {
      if (!finYearId) {
        toast.warn("Please select Financial Year");
        return;
      }

      try {
        const res = await axios.get(`${BASE_URL}/api/bonus-master/branches`, {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType: branchType,
          },
        });
        
        if (res?.data?.success) {
          const branchData = res.data.branches || [];
          setBranches(branchData);
          
          // Set user's branch as default
          if (branchId && branchData.length > 0) {
            const userBranch = branchData.find(b => 
              String(b.fld_branchid) === String(branchId)
            );
            if (userBranch) {
              setSelectedBranch(String(branchId));
            }
          }
        } else {
          toast.error(res?.data?.message || "Failed to load branches");
        }
      } catch (err) {
        toast.error("Failed to load branches");
        console.error("Branches Error:", err);
      }
    };
    
    fetchBranches();
  }, [finYearId, branchId, branchType]);

  // Fetch Departments
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!finYearId) return;

      try {
        const res = await axios.get(`${BASE_URL}/api/bonus-master/departments`, {
          params: {
            finYearId: Number(finYearId),
          },
        });
        
        if (res?.data?.success) {
          setDepartments(res.data.departments || []);
        } else {
          toast.error(res?.data?.message || "Failed to load departments");
        }
      } catch (err) {
        toast.error("Failed to load departments");
        console.error("Departments Error:", err);
      }
    };
    
    fetchDepartments();
  }, [finYearId]);

  const fetchData = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/bonus-master/data`, {
        params: {
          finYearId: Number(finYearId),
          empCode: empCode,
          empName: empName,
          branchId: selectedBranch !== "Select" ? selectedBranch : "",
          year: selectedYear !== "Select" ? selectedYear : "",
          paymentMode: selectedPayment,
          deptId: selectedDept,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load data");
        setRows([]);
        setTotal(0);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchData(1);
  };

  const handleViewAll = async () => {
    if (branchType !== "HO") {
      toast.info("View All is only for HO users");
      return;
    }
    
    setSelectedBranch("Select");
    setPage(1);
    await fetchData(1);
  };

  const handleExportExcel = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    if (rows.length === 0) {
      toast.warn("No data to export");
      return;
    }

    try {
      setLoading(true);
      toast.info("Exporting to Excel...");

      const res = await axios.get(`${BASE_URL}/api/bonus-master/export-excel`, {
        params: {
          finYearId: Number(finYearId),
          empCode: empCode,
          empName: empName,
          branchId: selectedBranch !== "Select" ? selectedBranch : "",
          year: selectedYear !== "Select" ? selectedYear : "",
          paymentMode: selectedPayment,
          deptId: selectedDept,
        },
      });

      if (res?.data?.success && res?.data?.data) {
        let excelData = res.data.data;

        // Remove Department column if exists
        excelData = excelData.map(row => {
          const { Department, ...rest } = row;
          return rest;
        });

        // Add S.No column
        excelData = excelData.map((row, idx) => ({
          "S.No": idx + 1,
          ...row
        }));

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Bonus Master");

        // Get branch name and year for filename
        const branchName = branches.find(b => b.fld_branchid.toString() === selectedBranch)?.fld_branchname || "All";
        const yearText = selectedYear !== "Select" ? selectedYear : new Date().getFullYear();

        // Download
        XLSX.writeFile(wb, `Bonus Report for the ${branchName} - ${yearText}.xlsx`);
        toast.success("Excel exported successfully!");
      } else {
        toast.error("No data to export");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Export failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (rows.length === 0) {
      toast.warn("No data to print");
      return;
    }

    // Calculate from and to year
    const toYear = selectedYear !== "Select" ? selectedYear : new Date().getFullYear().toString();
    const fromYear = (parseInt(toYear) - 1).toString();

    // Store data in localStorage for print page
    localStorage.setItem("bonusBranch", selectedBranch !== "Select" ? selectedBranch : "");
    localStorage.setItem("bonusFrmYear", fromYear);
    localStorage.setItem("bonusToYear", toYear);
    localStorage.setItem("bonusMasterPrintData", JSON.stringify(rows));

    // Open print window
    const printWindow = window.open(
      "/PayRoll/BonusMasterReport_Print",
      "_blank",
      "width=900,height=900,scrollbars=yes"
    );
    
    if (!printWindow) {
      toast.error("Please allow popups for this site");
    }
  };

  useEffect(() => {
    if (page > 1) {
      fetchData(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BONUS MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <FileText className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Bonus Master Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search, view and export employee bonus details with monthly breakdown
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchData(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              {branchType === "HO" && (
                <button onClick={handleViewAll} className={ghostBtn} disabled={loading}>
                  <Eye className="w-4 h-4" />
                  View All
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-5 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Employee Code */}
              <div>
                <label className={labelBase}>
                  <User className="w-4 h-4 inline mr-2" />
                  Employee Code
                </label>
                <input
                  value={empCode}
                  onChange={(e) => setEmpCode(e.target.value)}
                  placeholder="Enter employee code"
                  className={inputBase + " mt-2"}
                />
              </div>

              {/* Employee Name */}
              <div>
                <label className={labelBase}>
                  <User className="w-4 h-4 inline mr-2" />
                  Employee Name
                </label>
                <input
                  value={empName}
                  onChange={(e) => setEmpName(e.target.value)}
                  placeholder="Enter employee name"
                  className={inputBase + " mt-2"}
                />
              </div>

              {/* Payment Mode */}
              <div>
                <label className={labelBase}>
                  <CreditCard className="w-4 h-4 inline mr-2" />
                  Payment Mode
                </label>
                <select
                  value={selectedPayment}
                  onChange={(e) => setSelectedPayment(e.target.value)}
                  className={inputBase + " mt-2"}
                >
                  <option value="Select">Select</option>
                  <option value="1">Cash</option>
                  <option value="2">Bank</option>
                </select>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Site
                </label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className={inputBase + " mt-2"}
                  disabled={branchType !== "HO"}
                >
                  <option value="Select">Select</option>
                  {branches.map((b) => (
                    <option key={b.fld_branchid} value={b.fld_branchid}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label className={labelBase}>
                  <Briefcase className="w-4 h-4 inline mr-2" />
                  Department
                </label>
                <select
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                  className={inputBase + " mt-2"}
                >
                  <option value="Select">Select</option>
                  {departments.map((d) => (
                    <option key={d.fld_deptid} value={d.fld_deptid}>
                      {d.fld_departmentname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className={labelBase}>
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className={inputBase + " mt-2"}
                >
                  <option value="Select">Select</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3 justify-center mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>

              <button
                type="button"
                onClick={handleExportExcel}
                className={ghostBtn}
                disabled={loading || rows.length === 0}
              >
                <Download className="w-4 h-4" />
                Excel
              </button>

              <button
                type="button"
                onClick={handlePrint}
                className={ghostBtn}
                disabled={loading || rows.length === 0}
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-2 py-3 text-center font-bold text-xs">S.No</th>
                    <th className="px-2 py-3 text-center font-bold text-xs">Emp Code</th>
                    <th className="px-2 py-3 text-left font-bold text-xs">Emp Name</th>
                    <th className="px-2 py-3 text-center font-bold text-xs">Designation</th>
                    <th className="px-2 py-3 text-center font-bold text-xs">DOJ</th>
                    <th className="px-2 py-3 text-center font-bold text-xs">A/C No.</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Oct</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Nov</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Dec</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Jan</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Feb</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Mar</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Apr</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">May</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Jun</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Jul</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Aug</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Sep</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Total</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Batta</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Batta Bonus</th>
                    <th className="px-2 py-3 text-right font-bold text-xs">Total Bonus</th>
                    <th className="px-2 py-3 text-center font-bold text-xs">Location</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 23 }).map((__, c) => (
                          <td key={c} className="px-2 py-3">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={23}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <td className={`px-2 py-3 text-xs text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-2 py-3 text-xs text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.EmployeeCode || ""}
                        </td>
                        <td className={`px-2 py-3 text-xs text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r["Employee Name"] || ""}
                        </td>
                        <td className={`px-2 py-3 text-xs text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.Designation || ""}
                        </td>
                        <td className={`px-2 py-3 text-xs text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.DOJ || ""}
                        </td>
                        <td className={`px-2 py-3 text-xs text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r["A/C No."] || ""}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.October || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.November || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.December || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.January || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.February || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.March || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.April || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.May || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.June || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.July || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.August || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.September || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right font-bold ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.Total || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r.Batta || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r["Batta Bonus"] || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-right font-bold ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {parseFloat(r["Total Bonus"] || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-3 text-xs text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.Location || ""}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default BonusMaster;